import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileDown, FileX, Loader2 } from 'lucide-react';
import { useIsAdminTier, useIsReadOnly } from '@/shared/auth/SessionProvider';
import { useCanSeeMenu } from '@/shared/auth/useMenuPermissions';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { currencyFromSp, formatMoney, parseMoney } from '@/shared/lib/money';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import { uploadDocument } from '@/shared/api/documents';
import { proveedoresClient } from '@/shared/api/controllers/proveedores';
import { retencionesPagoClient } from '@/shared/api/controllers/retencionesPago';
import { enviarPagoFacturaEmail } from '@/features/administracion/api';
import { listCompras } from './api';
import { OrigenUnidadesSection } from '@/features/inventario/components/StockPorOrigen';
import {
  certificadoFileName,
  generateCertificadoRetencionPDF,
} from '@/features/administracion/pdf/certificadoRetencion';
import {
  generateReciboPagoPDF,
  reciboPagoFileName,
} from '@/features/administracion/pdf/reciboPago';
import { getCompraDetalle, type CompraDetailResponse } from './api';
import { CajaNombre } from '@/features/tesoreria/components/CajaNombre';

export function VerCompraPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const showRetenciones = activeCountry.features.has('retenciones');
  // La API de Importaciones es sólo admin/administración: el resto ve texto plano.
  const veMenuImportaciones = useCanSeeMenu('importaciones');
  const esAdminTier = useIsAdminTier();
  const puedeVerImportaciones = veMenuImportaciones && esAdminTier;

  /**
   * Origin of the navigation that landed us here. Admins typically arrive
   * from the compras list (no state), but the Administración module and
   * PagarFactura flow stash `{ from: '/administracion' }` in router state
   * so the back button returns the user where they came from. Chained
   * NC hops forward the same state so you can jump NC-to-NC and still
   * pop back out to admin in one click.
   */
  const backPath =
    typeof (location.state as { from?: unknown } | null)?.from === 'string'
      ? ((location.state as { from: string }).from)
      : '/compras';
  const backLabel =
    backPath === '/administracion' ? 'Volver a Administración' : 'Volver a Compras';
  // Preserve the origin on intra-compra hops (NC parent ↔ child, NC list).
  const forwardState = backPath !== '/compras' ? { from: backPath } : undefined;

  const compraQ = useQuery({
    queryKey: ['compras', 'detalle', id],
    queryFn: () => getCompraDetalle(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const moneda = currencyFromSp(compraQ.data?.header.fields.Moneda_CO);

  const totals = useMemo(() => {
    if (!compraQ.data) {
      return { pagado: 0, retenido: 0, ncPendientes: 0, restante: 0, total: 0 };
    }
    const total = parseMoney(compraQ.data.header.fields.Total_CO) ?? 0;
    const pagado = compraQ.data.pagos
      .filter((p) => p.fields.Status_PC !== 'Anulado')
      .reduce((s, p) => s + (parseMoney(p.fields.Importe_PC) ?? 0), 0);
    const retenido = compraQ.data.retenciones
      .filter((r) => r.fields.Status_RP === 'Activo' && !r.fields.IDPago_RP)
      .reduce((s, r) => s + (parseMoney(r.fields.ImporteRetenido_RP) ?? 0), 0);
    const ncPendientes = (compraQ.data.notasCreditoPendientes ?? []).reduce(
      (s, nc) => s + (parseMoney(nc.fields.Total_CO) ?? 0),
      0,
    );
    return {
      total,
      pagado,
      retenido,
      ncPendientes,
      restante: Math.max(0, total - pagado - retenido - ncPendientes),
    };
  }, [compraQ.data]);

  if (compraQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }
  if (compraQ.isError || !compraQ.data) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate(backPath)} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Volver
        </Button>
        <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center">
          <p className="text-base font-medium text-foreground">Compra no encontrada</p>
          <p className="mt-1 text-sm text-muted-foreground">
            La compra #{id} no existe o no tenés permisos para verla.
          </p>
        </div>
      </div>
    );
  }

  const {
    header,
    lines,
    percepciones,
    pagos,
    instrumentos,
    retenciones,
    notasCreditoPendientes = [],
    recepciones = [],
    importacion = null,
  } = compraQ.data;
  const f = header.fields;
  const isNC = (f.NotaCreditoID_CO ?? '').trim() !== '';

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-2">
      {/* Top bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <Button
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full bg-foreground text-background hover:bg-foreground/90"
          onClick={() => navigate(backPath)}
          aria-label={backLabel}
          title={backLabel}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          {isNC ? 'Nota de Crédito' : 'Compra'} #{header.id}
        </h1>
        <EstadoPill status={f.Status_CO ?? ''} />
        {isNC && (
          <button
            type="button"
            onClick={() =>
              navigate(`/compras/${f.NotaCreditoID_CO}`, { state: forwardState })
            }
            className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-violet-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-800 hover:bg-violet-100"
            title="Ver factura origen"
          >
            Aplicada a #{f.NotaCreditoID_CO}
          </button>
        )}
      </div>

      {/* Header card */}
      <div className="grid grid-cols-2 gap-3 rounded-2xl border bg-card p-4 md:grid-cols-4">
        <Info label="Proveedor" value={f.Proveedor_CO} />
        <Info label="Comprobante" value={`${f.Comprobante_CO ?? ''} ${f.NumeroComprobante_CO ?? ''}`.trim()} />
        <Info label="Fecha" value={f.FechaComprobante_CO} />
        {importacion && (
          <div className="space-y-0.5">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Importación
            </p>
            <p className="text-sm text-foreground">
              {puedeVerImportaciones ? (
                <Link
                  to={`/importaciones/${importacion.id}`}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  Importación N° {importacion.numero}
                </Link>
              ) : (
                <>Importación N° {importacion.numero}</>
              )}
              {importacion.descripcion ? ` · ${importacion.descripcion}` : ''}
            </p>
          </div>
        )}
        {f.FechaFiscal_CO && <Info label="Fecha Fiscal" value={f.FechaFiscal_CO} />}
        <Info label="Vencimiento" value={f.VencimientoPago_CO} />
        <Info label="Moneda" value={f.Moneda_CO} />
        {f.Moneda_CO === 'Dolares' && <Info label="Tipo de Cambio" value={f.TipoCambio_CO} />}
        <Info
          label="Lleva Factura"
          value={f.LlevaFactura_CO === 'SI' ? 'Sí' : 'No'}
        />
        <Info
          label="Débito Automático"
          value={f.DebitoAutomatico_CO === 'SI' ? 'Sí' : 'No'}
        />
        {f.Observaciones_CO && (
          <Info label="Observaciones" value={f.Observaciones_CO} className="md:col-span-4" />
        )}
        <div className="md:col-span-4">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Factura
          </p>
          {f.FacturaDriveItemID_CO && f.FacturaFileName_CO ? (
            <FileLink
              entityType="Compra"
              entityId={header.id}
              fileName={f.FacturaFileName_CO}
              onDeleted={() => qc.invalidateQueries({ queryKey: ['compras', 'detalle', header.id] })}
            />
          ) : (
            <FileUploader
              entityType="Compra"
              entityId={header.id}
              compact
              onUploaded={() =>
                qc.invalidateQueries({ queryKey: ['compras', 'detalle', header.id] })
              }
            />
          )}
        </div>
      </div>

      {/* Lines */}
      <Section title="Producto Servicio">
        {lines.length === 0 ? (
          <EmptyNote message="Sin líneas cargadas." />
        ) : (
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <Th>Artículo</Th>
                  <Th className="text-right">Cantidad</Th>
                  <Th className="text-right">Precio</Th>
                  <Th className="text-right">IVA</Th>
                  <Th className="text-right">Subtotal</Th>
                  <Th className="text-right">Total</Th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((l) => {
                  const precio = parseMoney(l.fields.Precio_PSC) ?? 0;
                  const cantidad = parseMoney(l.fields.Cantidad_PSC) ?? 0;
                  const subtotal = parseMoney(l.fields.MontoTotal_PSC) ?? precio * cantidad;
                  const ivaRaw = (l.fields.PorcentajeIVA_PSC ?? '').trim();
                  const ivaRate = /exento/i.test(ivaRaw)
                    ? 0
                    : Number(ivaRaw.replace('%', '').replace(',', '.'));
                  const llevaFactura = f.LlevaFactura_CO === 'SI';
                  const total =
                    llevaFactura && Number.isFinite(ivaRate)
                      ? subtotal + subtotal * (ivaRate / 100)
                      : subtotal;
                  return (
                    <tr key={l.id} className={l.fields.Status_PSC === 'Anulado' ? 'opacity-50 line-through' : ''}>
                      <Td>
                        <div className="font-medium text-foreground">
                          {l.fields.Descripcion_PSC || '—'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {l.fields.ProductoServicio_PSC} <span className="mx-1">›</span>{' '}
                          {l.fields.CentroCosto_PSC}
                          {l.fields.Observaciones_PSC ? ` · ${l.fields.Observaciones_PSC}` : ''}
                        </div>
                      </Td>
                      <Td className="text-right">{formatQty(cantidad)}</Td>
                      <Td className="text-right">{formatMoney(precio, moneda)}</Td>
                      <Td className="text-right">
                        {llevaFactura
                          ? `${l.fields.PorcentajeIVA_PSC || '—'}${l.fields.IvaCategoria_PSC === 'importacion' ? ' Imp.' : ''}`
                          : '—'}
                      </Td>
                      <Td className="text-right">{formatMoney(subtotal, moneda)}</Td>
                      <Td className="text-right font-semibold">{formatMoney(total, moneda)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Percepciones */}
      {percepciones.length > 0 && (
        <Section title="Percepciones e Impuestos">
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <Th>Percepción / Impuesto</Th>
                  <Th>Observaciones</Th>
                  <Th className="text-right">Importe</Th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {percepciones.map((p) => (
                  <tr key={p.id} className={p.fields.Status_PIC === 'Anulado' ? 'opacity-50 line-through' : ''}>
                    <Td className="font-medium text-foreground">
                      {p.fields.PercepcionImpuesto_PIC || '—'}
                    </Td>
                    <Td className="text-muted-foreground">
                      {p.fields.Observaciones_PIC || '—'}
                    </Td>
                    <Td className="text-right font-semibold">
                      {formatMoney(parseMoney(p.fields.Importe_PIC) ?? 0, moneda)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Notas de Crédito pendientes */}
      {notasCreditoPendientes.length > 0 && (
        <Section title={`Notas de Crédito aplicadas (${notasCreditoPendientes.length})`}>
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-violet-50/60 text-left text-xs uppercase tracking-wide text-violet-800">
                <tr>
                  <Th>Fecha</Th>
                  <Th>Nº Comprobante</Th>
                  <Th>Estado</Th>
                  <Th className="text-right">Monto</Th>
                  <Th className="w-16 text-right">Ver</Th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {notasCreditoPendientes.map((nc) => {
                  const ncMoneda = currencyFromSp(nc.fields.Moneda_CO);
                  return (
                    <tr key={nc.id} className="bg-violet-50/20">
                      <Td className="text-muted-foreground">
                        {nc.fields.FechaComprobante_CO || '—'}
                      </Td>
                      <Td className="font-medium text-foreground">
                        {nc.fields.NumeroComprobante_CO || `#${nc.id}`}
                      </Td>
                      <Td>
                        <EstadoPill status={nc.fields.Status_CO ?? ''} />
                      </Td>
                      <Td className="text-right font-semibold">
                        {formatMoney(parseMoney(nc.fields.Total_CO) ?? 0, ncMoneda)}
                      </Td>
                      <Td className="text-right">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/compras/${nc.id}`, { state: forwardState })
                          }
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                          title="Ver Nota de Crédito"
                          aria-label="Ver Nota de Crédito"
                        >
                          →
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Recepciones — auto-created on compra save for lines with ImpactaStock='SI' */}
      {recepciones.length > 0 && (
        <Section title={`Recepciones (${recepciones.length})`}>
          {recepciones.length === 0 ? (
            <EmptyNote message="Sin recepciones generadas." />
          ) : (
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <Th>ID</Th>
                    <Th>Fecha</Th>
                    <Th>Estado</Th>
                    <Th className="w-16 text-right">Ver</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recepciones.map((rec) => (
                    <tr key={rec.id}>
                      <Td className="font-medium text-foreground">#{rec.id}</Td>
                      <Td className="text-muted-foreground">{rec.fecha || '—'}</Td>
                      <Td>
                        <EstadoPill status={rec.status} />
                      </Td>
                      <Td className="text-right">
                        <button
                          type="button"
                          onClick={() => navigate('/recepcion')}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                          title="Ir a Recepciones"
                          aria-label="Ir a Recepciones"
                        >
                          →
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* Mercadería recibida de esta compra y a dónde fue (stock por origen). */}
      {f.Status_CO !== 'Anulado' && (
        <Section title="Mercadería recibida">
          <OrigenUnidadesSection kind="compra" id={header.id} />
        </Section>
      )}

      {/* Retenciones — shows all retentions for this compra, whether linked
          to a pago or standalone. Per-pago retentions are also rendered
          inline inside PagoCard below for context. Country-gated: AR's
          regime has retenciones (Ganancias, IIBB, IVA, etc.); MX's CFDI
          flow does not, so the section is hidden for MX users. */}
      {showRetenciones && retenciones.length > 0 && (
        <Section title={`Retenciones (${retenciones.length})`}>
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <Th>Tipo / Concepto</Th>
                  <Th>Nº Comprobante</Th>
                  <Th>Pago</Th>
                  <Th className="text-right">Importe</Th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {retenciones.map((r) => {
                  const isAnulated = r.fields.Status_RP === 'Anulado';
                  return (
                    <tr
                      key={r.id}
                      className={isAnulated ? 'opacity-50 line-through' : ''}
                    >
                      <Td>
                        <div className="font-medium text-foreground">
                          {r.fields.Tipo_RP || '—'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {r.fields.Concepto_RP || '—'}
                        </div>
                      </Td>
                      <Td>{r.fields.NroComprobante_RP || '—'}</Td>
                      <Td className="text-muted-foreground">
                        {r.fields.IDPago_RP ? `Pago #${r.fields.IDPago_RP}` : 'Sin pago'}
                      </Td>
                      <Td className="text-right font-semibold">
                        {formatMoney(parseMoney(r.fields.ImporteRetenido_RP) ?? 0, 'ARS')}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Pagos */}
      <Section title={`Pagos (${pagos.length})`}>
        {pagos.length === 0 ? (
          <EmptyNote message="Aún no se registraron pagos para esta compra." />
        ) : (
          <div className="space-y-3">
            {pagos.map((p) => (
              <PagoCard
                key={p.id}
                pago={p}
                compra={header}
                instrumentos={instrumentos.filter((i) => i.fields.IDPago_IP === p.id)}
                retenciones={retenciones.filter((r) => r.fields.IDPago_RP === p.id)}
                moneda={moneda}
                onRefresh={() =>
                  qc.invalidateQueries({ queryKey: ['compras', 'detalle', header.id] })
                }
              />
            ))}
          </div>
        )}
      </Section>

      {/* Footer totals */}
      <div className="sticky bottom-0 mt-2 flex shrink-0 flex-wrap items-center gap-3 rounded-2xl border bg-card px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <TotalPill label="Total">{formatMoney(totals.total, moneda)}</TotalPill>
          <TotalPill label="Pagado">{formatMoney(totals.pagado, moneda)}</TotalPill>
          {showRetenciones && totals.retenido > 0 && (
            <TotalPill label="Retenciones (sin pago)">{formatMoney(totals.retenido, moneda)}</TotalPill>
          )}
          {totals.ncPendientes > 0 && (
            <TotalPill label="Notas de Crédito">
              {formatMoney(totals.ncPendientes, moneda)}
            </TotalPill>
          )}
          <TotalPill
            label="Saldo"
            emphasize={totals.restante > 0.01}
          >
            {formatMoney(totals.restante, moneda)}
          </TotalPill>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function PagoCard({
  pago,
  compra,
  instrumentos,
  retenciones,
  moneda,
  onRefresh,
}: {
  pago: CompraDetailResponse['pagos'][number];
  compra: CompraDetailResponse['header'];
  instrumentos: CompraDetailResponse['instrumentos'];
  retenciones: CompraDetailResponse['retenciones'];
  moneda: 'ARS' | 'MXN' | 'USD';
  onRefresh: () => void;
}) {
  const f = pago.fields;
  const isAnulado = f.Status_PC === 'Anulado';
  const { config: country } = useActiveCountry();
  const showRetenciones = country.features.has('retenciones');
  return (
    <div
      className={cn(
        'rounded-xl border bg-card p-3',
        isAnulado && 'opacity-50',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="text-muted-foreground">Pago #{pago.id}</span>
          <span className="mx-2 text-muted-foreground">·</span>
          <span className="text-foreground">{f.FechaPago_PC || '—'}</span>
          {f.Recibo_PC && (
            <>
              <span className="mx-2 text-muted-foreground">·</span>
              <span className="text-muted-foreground">Recibo {f.Recibo_PC}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
              isAnulado
                ? 'bg-slate-200 text-slate-600'
                : 'bg-emerald-100 text-emerald-700',
            )}
          >
            {f.Status_PC || 'Pagado'}
          </span>
          <span className="text-sm font-semibold text-foreground">
            {formatMoney(parseMoney(f.Importe_PC) ?? 0, moneda)}
          </span>
          {!isAnulado && (
            <ReciboDownloadButton
              pago={pago}
              compra={compra}
              instrumentos={instrumentos}
              retenciones={retenciones}
            />
          )}
        </div>
      </div>
      {instrumentos.length > 0 && (
        // `min-w-0` so a long file pill inside can shrink instead of
        // pushing the card past its container's width.
        <div className="mt-2 min-w-0 rounded-lg border bg-background">
          <div className="border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Instrumentos
          </div>
          <ul className="divide-y text-sm">
            {instrumentos.map((i) => {
              const driveId = i.fields.ComprobanteDriveItemID_IP;
              const fileName = i.fields.ComprobanteFileName_IP;
              return (
                <li
                  key={i.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5"
                >
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-foreground"><CajaNombre caja={i.fields.Cuenta_IP} soloMarca /></span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({i.fields.TipoCuenta_IP})
                    </span>
                  </span>
                  <span className="font-semibold">
                    {formatMoney(parseMoney(i.fields.Importe_IP) ?? 0, moneda)}
                  </span>
                  {/* Per-instrumento comprobante (bank receipt). Reuses
                      the shared FileLink/FileUploader pair via the
                      `Instrumento` entity registered in documents.ts.
                      Wrapped in a width-bounded block (`w-full min-w-0`)
                      so a long filename truncates inside the pill rather
                      than overflowing the card. */}
                  {!isAnulado && (
                    <div className="w-full min-w-0">
                      {driveId && fileName ? (
                        <FileLink
                          entityType="Instrumento"
                          entityId={i.id}
                          fileName={fileName}
                          onDeleted={onRefresh}
                          compact
                        />
                      ) : (
                        <FileUploader
                          entityType="Instrumento"
                          entityId={i.id}
                          compact
                          onUploaded={onRefresh}
                        />
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {showRetenciones && retenciones.length > 0 && (
        <div className="mt-2 rounded-lg border bg-background">
          <div className="border-b px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Retenciones
          </div>
          <ul className="divide-y text-sm">
            {retenciones.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <span className="flex-1">
                  <span className="font-medium text-foreground">{r.fields.Tipo_RP}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {r.fields.Concepto_RP}
                  </span>
                </span>
                <span className="font-semibold">
                  {formatMoney(parseMoney(r.fields.ImporteRetenido_RP) ?? 0, 'ARS')}
                </span>
                <RetencionCertSlot
                  retencion={r}
                  compra={compra}
                  onRefresh={onRefresh}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pago-header comprobante slot intentionally absent — PowerApps
          attaches receipts per-instrumento (`ComprobanteAdjunto_IP`),
          not at the pago level. The instrumento list above already
          surfaces those per-row. The legacy `ComprobanteDriveItemID_PC`
          column on `08.PagosCompras` stays in place for any historical
          uploads but is no longer surfaced or accepted here. */}
    </div>
  );
}

/**
 * Compact "Descargar recibo" button on each active pago. Unlike the
 * retention cert, the recibo is a proveedor-courtesy doc (not AFIP-
 * required), so we only generate+download — no SharePoint archival.
 * Admins re-click to regenerate if they need a fresh copy.
 */
function ReciboDownloadButton({
  pago,
  compra,
  instrumentos,
  retenciones,
}: {
  pago: CompraDetailResponse['pagos'][number];
  compra: CompraDetailResponse['header'];
  instrumentos: CompraDetailResponse['instrumentos'];
  retenciones: CompraDetailResponse['retenciones'];
}) {
  const { config: activeCountry } = useActiveCountry();
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      const blob = await generateReciboPagoPDF({
        pago,
        compra,
        instrumentos,
        retenciones,
        country: activeCountry.code,
      });
      const fileName = reciboPagoFileName(pago.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('No se pudo generar el recibo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title="Descargar recibo de pago"
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <FileDown className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
}

/**
 * Compact "Descargar certificado" button that, on first click, generates
 * the retention PDF via jsPDF, triggers a browser download, AND uploads
 * the blob to SharePoint (so later views skip the regeneration path).
 * Once `CertificadoDriveItemID_RP` is set, swaps itself for a `<FileLink>`
 * pointing at the stored PDF.
 */
function RetencionCertSlot({
  retencion,
  compra,
  onRefresh,
}: {
  retencion: CompraDetailResponse['retenciones'][number];
  compra: CompraDetailResponse['header'];
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const isReadOnly = useIsReadOnly();
  const existingCert = retencion.fields.CertificadoDriveItemID_RP;
  const existingFileName = retencion.fields.CertificadoFileName_RP;

  if (existingCert && existingFileName) {
    return (
      <FileLink
        entityType="Retencion"
        entityId={retencion.id}
        fileName={existingFileName}
        onDeleted={onRefresh}
        compact
      />
    );
  }

  if (isReadOnly) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <FileX className="h-3.5 w-3.5" aria-hidden />
        Sin archivo cargado
      </span>
    );
  }

  const handleGenerate = async () => {
    setBusy(true);
    try {
      // Look up the proveedor row for CUIT + address. Filter client-side
      // by name match — Proveedor_CO stores the display name, same as
      // Nombre_P in the catalog.
      const proveedorName = (compra.fields.Proveedor_CO ?? '').trim();
      const thisFecha = retencion.fields.FechaComprobante_RP ?? '';
      const thisMesAnio = monthYearFromArDate(thisFecha); // "mm/yyyy" or ''

      // Fetch cross-compra monthly series: all RetencionesPago for
      // this proveedor, then filter to the same month. Join each with
      // its Compra to pull NumeroComprobante / FechaComprobante / Total
      // for the PDF's bottom table.
      const [allProveedores, monthRetenciones, allCompras] = await Promise.all([
        proveedoresClient.listItems({ top: 5000 }),
        retencionesPagoClient
          .listItems({
            filter: `fields/Proveedor_RP eq '${proveedorName.replace(/'/g, "''")}' and fields/Status_RP eq 'Activo'`,
          })
          .catch(() => retencionesPagoClient.listItems({ top: 5000 })),
        listCompras(),
      ]);

      const proveedor =
        allProveedores.find(
          (p) => (p.fields.Nombre_P ?? '').trim() === proveedorName,
        )?.fields ?? null;

      const comprasById = new Map(allCompras.map((c) => [c.id, c.fields]));
      // Los comprobantes que forman el acumulado de ESTA retención (RG 830):
      // mismo proveedor + mismo concepto + mes del pago, ya pagados. El
      // proveedor se vuelve a chequear acá porque el fallback trae todas las
      // filas — así el certificado de un proveedor listaba las facturas de
      // todos los demás de ese mes.
      const concepto = (retencion.fields.Concepto_RP ?? '').trim();
      const sameMonth = monthRetenciones.filter((r) => {
        if ((r.fields.Status_RP ?? '') !== 'Activo') return false;
        if ((r.fields.Proveedor_RP ?? '').trim() !== proveedorName) return false;
        if ((r.fields.Concepto_RP ?? '').trim() !== concepto) return false;
        // `IDPago_RP='Pendiente'`/vacío = cálculo sin pagar, no una retención.
        const pago = (r.fields.IDPago_RP ?? '').trim();
        if (!pago || pago === 'Pendiente') return false;
        return monthYearFromArDate(r.fields.FechaComprobante_RP) === thisMesAnio;
      });
      const mesAnioSeries = sameMonth.map((r) => {
        const parentCompra = r.fields.IDCompra_RP
          ? comprasById.get(r.fields.IDCompra_RP)
          : undefined;
        return {
          id: r.id,
          fields: {
            ...r.fields,
            NumeroComprobante_CO: parentCompra?.NumeroComprobante_CO ?? null,
            FechaComprobante_CO: parentCompra?.FechaComprobante_CO ?? null,
            Total_CO: parentCompra?.Total_CO ?? null,
            Moneda_CO: parentCompra?.Moneda_CO ?? null,
          },
        };
      });

      const blob = await generateCertificadoRetencionPDF({
        retencion,
        compra,
        proveedor,
        mesAnioSeries,
      });
      const fileName = certificadoFileName(retencion.id);

      // Download + upload in parallel.
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);

      await uploadDocument({
        entityType: 'Retencion',
        entityId: retencion.id,
        file: blob,
        fileName,
      });
      // Now that the retención PDF is in place, re-fire the
      // pago-factura email so it ships with both attachments. Server
      // is fail-silent (returns `sent:false` with a reason). Never
      // blocks the cert-generation flow; we surface the outcome as
      // part of the *same* toast as the cert success so the admin
      // never sees two stacked toasts at once.
      const pagoId = (retencion.fields.IDPago_RP ?? '').trim();
      let emailNote: string | null = null;
      if (pagoId && pagoId !== 'Pendiente') {
        try {
          const emailResult = await enviarPagoFacturaEmail(pagoId);
          if (!emailResult.sent && emailResult.reason !== 'retencion_pending') {
            emailNote =
              emailResult.reason === 'no_proveedor_email'
                ? 'el proveedor no tiene correo cargado'
                : emailResult.reason === 'pdf_fetch_failed'
                  ? 'no se pudo recuperar un PDF adjunto'
                  : 'falló el envío';
          }
        } catch (err) {
          console.warn('[retencion-cert] email auto-fire network error:', err);
          emailNote = 'hubo un problema de red al enviar el correo';
        }
      }
      if (emailNote) {
        toast.warning('Certificado generado, correo no enviado', {
          description: `${emailNote.charAt(0).toUpperCase() + emailNote.slice(1)}.`,
        });
      } else {
        toast.success('Certificado generado y archivado');
      }
      onRefresh();
    } catch (err) {
      toast.error('No se pudo generar el certificado', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGenerate}
      disabled={busy}
      title="Descargar certificado de retención"
      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <FileDown className="h-3.5 w-3.5" aria-hidden />
      )}
      Certificado
    </button>
  );
}

/** Extract `mm/yyyy` from a `dd/mm/yyyy` string. Returns '' on miss. */
function monthYearFromArDate(s: string | null | undefined): string {
  if (!s) return '';
  const m = s.match(/^\d{2}\/(\d{2}\/\d{4})$/);
  return m ? m[1] : '';
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function Info({
  label,
  value,
  className,
}: {
  label: string;
  value?: string | null;
  className?: string;
}) {
  return (
    <div className={cn('space-y-0.5', className)}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm text-foreground">{value && value.trim() ? value : '—'}</p>
    </div>
  );
}

function Section({
  title,
  children,
  right,
}: {
  title: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function EmptyNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

function EstadoPill({ status }: { status: string }) {
  const cls =
    status === 'Pagado'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'Pago Parcial'
        ? 'bg-amber-100 text-amber-700'
        : status === 'Pendiente'
          ? 'bg-amber-100 text-amber-700'
          : status === 'Anulado'
            ? 'bg-slate-200 text-slate-600'
            : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {status || '—'}
    </span>
  );
}

function TotalPill({
  label,
  children,
  emphasize,
}: {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-lg border px-3',
        emphasize ? 'border-amber-300 bg-amber-50' : 'bg-secondary',
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-semibold', emphasize ? 'text-amber-800' : 'text-foreground')}>
        {children}
      </span>
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-2.5 font-semibold', className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2.5', className)}>{children}</td>;
}

function formatQty(n: number): string {
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
